import React, { useEffect, useRef, useState } from "react";
import Slider from "@mui/material/Slider";
import { formatMet } from "./artemis-mission";
import "./MissionTimeline.css";

// Labels under the slider; the other milestones are unlabelled ticks.
const LABELLED = { launch: "Launch", tli: "To the Moon", flyby: "Flyby", splashdown: "Splashdown" };

const formatKm = (km) => `${Math.round(km).toLocaleString("en-GB")} km`;
const utc = (ms) => new Date(ms).toLocaleString("en-GB", {
  timeZone: "UTC", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
}) + " UTC";

/**
 * The mission replay's timeline, in place of the news timeline: plays the
 * mission (`model`, artemis-mission.js) after `startDelayMs`, scrubs, and
 * writes the moment shown to `clock` ({ u, ms }) for the globe to follow.
 */
export default function MissionTimeline({ model, clock, startDelayMs = 0, onExit }) {
  const [u, setU] = useState(0);
  const [playing, setPlaying] = useState(false);
  const uRef = useRef(0);
  const scrubbing = useRef(false);
  const state = useRef({});

  const show = (next) => {
    uRef.current = next;
    clock.current = { u: next, ms: model.timeAt(next) };
    setU(next);
  };

  useEffect(() => {
    show(0);
    const timer = setTimeout(() => setPlaying(true), startDelayMs);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model]);

  useEffect(() => {
    if (!playing) return;
    let raf;
    let last = performance.now();
    const tick = (now) => {
      const dt = Math.min(0.1, (now - last) / 1000);
      last = now;
      if (!scrubbing.current) {
        const next = Math.min(model.playbackSeconds, uRef.current + dt);
        show(next);
        if (next >= model.playbackSeconds) { setPlaying(false); return; }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, model]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onExit();
      if (e.key === " " && e.target === document.body) { e.preventDefault(); togglePlay(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const togglePlay = () => {
    if (!playing && uRef.current >= model.playbackSeconds) show(0);
    setPlaying(!playing);
  };

  const s = model.stateAt(model.timeAt(u), state.current);
  const nearMoon = s.moonAltitudeKm < s.altitudeKm;
  const marks = model.milestones.map((m) => ({ value: m.playback, label: LABELLED[m.id] }));
  const current = [...model.milestones].reverse().find((m) => m.playback <= u + 0.05);

  return (
    <div className="timeline-panel ui-panel mission-timeline" role="group" aria-label="Artemis II mission replay">
      <div className="mission-timeline__top">
        <button type="button" className="mission-timeline__play" onClick={togglePlay} aria-label={playing ? "Pause" : "Play"}>
          {playing ? (
            <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="6" y="5" width="4" height="14" rx="1" /><rect x="14" y="5" width="4" height="14" rx="1" /></svg>
          ) : (
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5.5v13l11-6.5z" /></svg>
          )}
        </button>
        <div className="mission-timeline__readout" aria-live="off">
          <span className="mission-timeline__phase">Artemis II · {s.phase}</span>
          <span className="mission-timeline__stats">
            <span>{formatMet(s.t)}</span>
            <span>{utc(s.ms)}</span>
            <span>{nearMoon ? `${formatKm(s.moonAltitudeKm)} above the Moon` : `${formatKm(Math.max(0, s.altitudeKm))} up`}</span>
            <span>{Math.round(s.speedKmS * 3600).toLocaleString("en-GB")} km/h</span>
          </span>
          {current && current.id !== "launch" && <span className="mission-timeline__milestone">{current.label}{current.km ? ` · ${formatKm(current.km)}` : ""}</span>}
        </div>
        <button type="button" className="mission-timeline__exit" onClick={onExit}>Back to news</button>
      </div>
      <div className="timeline-slider">
        <Slider
          min={0}
          max={model.playbackSeconds}
          step={0.01}
          value={u}
          marks={marks}
          onChange={(_, value) => { scrubbing.current = true; show(value); }}
          onChangeCommitted={() => { scrubbing.current = false; }}
          getAriaLabel={() => "Mission time"}
          getAriaValueText={() => `${s.phase}, ${formatMet(s.t)}`}
          sx={{
            color: "#fff",
            "& .MuiSlider-thumb": { width: 14, height: 14, boxShadow: "0 0 0 4px rgba(255, 255, 255, 0.25)" },
            "& .MuiSlider-track": { height: 3, border: 0 },
            "& .MuiSlider-rail": { height: 3, opacity: 0.35 },
            "& .MuiSlider-mark": { backgroundColor: "rgba(255, 255, 255, 0.7)", height: 7, width: 2 },
            "& .MuiSlider-markLabel": { color: "rgba(255, 255, 255, 0.75)", fontFamily: "inherit", fontSize: "12px" },
            "& .MuiSlider-markLabelActive": { color: "#fff" },
            // Keep the end labels inside the panel.
            "& .MuiSlider-markLabel[data-index='0']": { transform: "translateX(0)" },
            [`& .MuiSlider-markLabel[data-index='${marks.length - 1}']`]: { transform: "translateX(-100%)" },
          }}
        />
      </div>
    </div>
  );
}
